/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { mcpApprovalCreateRequest } from "../funcs/mcpApprovalCreateRequest.js";
import { mcpApprovalEnsureServerReview } from "../funcs/mcpApprovalEnsureServerReview.js";
import { mcpApprovalGetRequest } from "../funcs/mcpApprovalGetRequest.js";
import { mcpApprovalListRequests } from "../funcs/mcpApprovalListRequests.js";
import { mcpApprovalPromote } from "../funcs/mcpApprovalPromote.js";
import { mcpApprovalRecordDecision } from "../funcs/mcpApprovalRecordDecision.js";
import { mcpApprovalRefreshEvidence } from "../funcs/mcpApprovalRefreshEvidence.js";
import { mcpApprovalStartResearch } from "../funcs/mcpApprovalStartResearch.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { ApprovalDecision } from "../models/components/approvaldecision.js";
import { ApprovalRequestDetail } from "../models/components/approvalrequestdetail.js";
import { ApprovalRequestSummary } from "../models/components/approvalrequestsummary.js";
import { ListApprovalRequestsResult } from "../models/components/listapprovalrequestsresult.js";
import { ResearchReport } from "../models/components/researchreport.js";
import {
  CreateMcpApprovalRequestRequest,
  CreateMcpApprovalRequestSecurity,
} from "../models/operations/createmcpapprovalrequest.js";
import {
  EnsureMcpServerReviewRequest,
  EnsureMcpServerReviewSecurity,
} from "../models/operations/ensuremcpserverreview.js";
import {
  GetMcpApprovalRequestRequest,
  GetMcpApprovalRequestSecurity,
} from "../models/operations/getmcpapprovalrequest.js";
import {
  ListMcpApprovalRequestsRequest,
  ListMcpApprovalRequestsSecurity,
} from "../models/operations/listmcpapprovalrequests.js";
import {
  PromoteMcpApprovalRequestRequest,
  PromoteMcpApprovalRequestSecurity,
} from "../models/operations/promotemcpapprovalrequest.js";
import {
  RecordMcpApprovalDecisionRequest,
  RecordMcpApprovalDecisionSecurity,
} from "../models/operations/recordmcpapprovaldecision.js";
import {
  RefreshMcpApprovalEvidenceRequest,
  RefreshMcpApprovalEvidenceSecurity,
} from "../models/operations/refreshmcpapprovalevidence.js";
import {
  StartMcpResearchRequest,
  StartMcpResearchSecurity,
} from "../models/operations/startmcpresearch.js";
import { unwrapAsync } from "../types/fp.js";

export class McpApproval extends ClientSDK {
  /**
   * createRequest mcpApproval
   *
   * @remarks
   * Ask for an MCP server to be reviewed. Repeat asks for the same server attach to the existing review rather than opening a second one.
   */
  async createRequest(
    request: CreateMcpApprovalRequestRequest,
    security?: CreateMcpApprovalRequestSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ApprovalRequestSummary> {
    return unwrapAsync(mcpApprovalCreateRequest(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * ensureServerReview mcpApproval
   *
   * @remarks
   * Resolve the evidence dossier for a server URL, opening one when none exists. Gathers evidence without recording any ask or decision, so a server can be inspected before — or without — anyone requesting it.
   */
  async ensureServerReview(
    request: EnsureMcpServerReviewRequest,
    security?: EnsureMcpServerReviewSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ApprovalRequestSummary> {
    return unwrapAsync(mcpApprovalEnsureServerReview(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getRequest mcpApproval
   *
   * @remarks
   * Fetch one MCP approval request with its evidence and decision history.
   */
  async getRequest(
    request: GetMcpApprovalRequestRequest,
    security?: GetMcpApprovalRequestSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ApprovalRequestDetail> {
    return unwrapAsync(mcpApprovalGetRequest(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listRequests mcpApproval
   *
   * @remarks
   * List MCP approval requests for a project.
   */
  async listRequests(
    request?: ListMcpApprovalRequestsRequest | undefined,
    security?: ListMcpApprovalRequestsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListApprovalRequestsResult> {
    return unwrapAsync(mcpApprovalListRequests(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * promote mcpApproval
   *
   * @remarks
   * Promote a risk-policy bypass request into an approval request, carrying its requester and justification into the review queue.
   */
  async promote(
    request: PromoteMcpApprovalRequestRequest,
    security?: PromoteMcpApprovalRequestSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ApprovalRequestSummary> {
    return unwrapAsync(mcpApprovalPromote(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * recordDecision mcpApproval
   *
   * @remarks
   * Approve or deny an MCP approval request, recording the rationale and who it applies to.
   */
  async recordDecision(
    request: RecordMcpApprovalDecisionRequest,
    security?: RecordMcpApprovalDecisionSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ApprovalDecision> {
    return unwrapAsync(mcpApprovalRecordDecision(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * refreshEvidence mcpApproval
   *
   * @remarks
   * Re-run every evidence source for a request and replace its current evidence with the fresh gather. Frozen decision snapshots are never touched.
   */
  async refreshEvidence(
    request: RefreshMcpApprovalEvidenceRequest,
    security?: RefreshMcpApprovalEvidenceSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ApprovalRequestDetail> {
    return unwrapAsync(mcpApprovalRefreshEvidence(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * startResearch mcpApproval
   *
   * @remarks
   * Start a research-agent run for an approval request. The agent searches the web and reads pages about the server's vendor, then files a cited report; it never decides. Runs are additive — a re-run adds a report rather than replacing one — and at most one run per request is in flight at a time: starting while one runs returns the running report.
   */
  async startResearch(
    request: StartMcpResearchRequest,
    security?: StartMcpResearchSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ResearchReport> {
    return unwrapAsync(mcpApprovalStartResearch(
      this,
      request,
      security,
      options,
    ));
  }
}
